/**
 * Lo que la capa expone y **no es un componente**: providers de Base UI, los `Close`s, las piezas
 * sueltas de `Field` y `Menu`, y el hook de toasts.
 *
 * Viven acá y no junto a su componente por una razón mecánica: la regla `react-refresh` de ESLint
 * exige que un archivo exporte solo componentes o solo valores, y no puede distinguir un provider
 * re-exportado de una constante cualquiera. En `src/au/` la regla está en `error`, así que un
 * archivo sin componentes es la única forma de cumplirla sin apagarla. Es el mismo motivo por el que
 * existen `styles/tones.ts` y `styles/chip-badge.ts`.
 */
import { Dialog } from '@base-ui/react/dialog'
import { Field } from '@base-ui/react/field'
import { Menu } from '@base-ui/react/menu'
import { Toast } from '@base-ui/react/toast'
import { useCallback, useMemo } from 'react'
import type { FeedbackTone } from './styles/feedback-tone'
import type { ToastMessage } from './toast'

/** Cierra el diálogo desde el pie sin tener que manejar `open` a mano:
 *  `<DialogClose render={<Button>Cancelar</Button>} />`. */
export const DialogClose = Dialog.Close

/** Las partes crudas de `Field`, para los campos que no entran en la forma corriente. */
export const FieldParts = {
  Root: Field.Root,
  Label: Field.Label,
  Control: Field.Control,
  Description: Field.Description,
  Error: Field.Error,
  Validity: Field.Validity,
}

export const MenuGroup = Menu.Group
export const MenuRadioGroup = Menu.RadioGroup

/**
 * Provider del sistema de toasts. Va una sola vez, envolviendo la página: todos los toasts
 * comparten un viewport, y ahí es donde se apilan.
 */
export const ToastProvider = Toast.Provider

/** Encola un toast y devuelve su id, que es lo que Base UI pide para cerrarlo o actualizarlo. */
export type ShowToast = (message: ToastMessage) => string

export interface ToastActions {
  /** Feedback que pide atención, en `primary` (el azul). */
  showNotice: ShowToast
  /** Feedback de contexto, en `info` (sin color). */
  showInfo: ShowToast
  showSuccess: ShowToast
  showWarning: ShowToast
  showError: ShowToast
}

/**
 * **La API de toasts de la capa se pide por intención, no por tono.** Cinco funciones con nombre en
 * vez del `toastManager.add({ …, type })` crudo de Base UI.
 *
 * La diferencia no es azúcar. Con `add()` el tono es un `string` libre: un `type: 'inf0'` compila, se
 * encola y sale un toast sin filo, y nada lo detecta. Cada una de estas cinco fija su tono adentro,
 * así que quien llama elige entre cinco intenciones y no puede escribir un tono inválido.
 *
 * Es también la razón por la que `useToastManager` no se re-exporta: mientras esté disponible, la
 * ruta cruda es la más corta. Adentro se usa —es de donde sale el `add`—, pero como detalle de
 * implementación.
 *
 * Las cinco funciones son estables entre renders. No es un requisito del componente sino de sus
 * consumidores: un `useEffect` o un `useCallback` que las liste como dependencia no debería
 * re-dispararse en cada render de la página, y sin `useMemo` lo haría.
 */
export function useToastActions(): ToastActions {
  const { add } = Toast.useToastManager()

  const show = useCallback(
    (type: FeedbackTone): ShowToast =>
      ({ title, description }) =>
        add({ title, description, type }),
    [add],
  )

  return useMemo(
    () => ({
      showNotice: show('primary'),
      showInfo: show('info'),
      showSuccess: show('success'),
      showWarning: show('warning'),
      showError: show('error'),
    }),
    [show],
  )
}
